import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Text,
  Icons,
  Loader,
  Button,
  TextInput,
} from "@pushpress/shared-components";
import { NameSuggestionsQueryResult, UpdateClubCallback } from "./types";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import { CommittedClubEvent } from "./CommittedClubEvents";

type SetupNamePaneProps = {
  isClubSetup: boolean;
  hasLaunched: boolean;
  suggestionsHook: NameSuggestionsQueryResult;
  value: string | undefined;
  setName: React.Dispatch<React.SetStateAction<string>>;
  closeDrawer: (trackClose: boolean) => void;
  updateClub: UpdateClubCallback;
};

function SetupNamePane(props: SetupNamePaneProps) {
  const {
    value,
    closeDrawer,
    setName,
    suggestionsHook,
    isClubSetup,
    hasLaunched,
    updateClub,
  } = props;

  const [error, setError] = useState<string | null>(null);

  const [name, setNameLocal] = useState<string>(value ?? "Committed Club");

  const {
    data: nameSuggestions,
    isLoading,
    isRefetching,
    isError,
    nextPage,
  } = suggestionsHook;

  const allGeneratedNames = useMemo(() => new Set<string>(), []);

  useEffect(() => {
    if (isError || !nameSuggestions) return;
    nameSuggestions.forEach((s) => allGeneratedNames.add(s));
  }, [allGeneratedNames, nameSuggestions, isError]);

  const { trackEvent } = useAnalytics();

  const validate = useCallback((text: string) => {
    if (text.trim().length === 0) {
      return setError("Name required");
    } else if (text.length > 30) {
      return setError("Title cannot exceed 30 characters");
    } else {
      return setError(null);
    }
  }, []);

  const validateAndSetName = useCallback(
    (text: string) => {
      setNameLocal(text);
      validate(text);
    },
    [validate],
  );

  const refreshNames = useCallback(async () => {
    nextPage();
    trackEvent(CommittedClubEvent.Refreshed, {
      app: "committed_club",
      refresh: "ai_generated_names",
      ai_generated: true,
      live_now: isClubSetup,
      state: hasLaunched ? "launched" : "pre_launch",
      page: "name_panel",
    });
  }, [hasLaunched, isClubSetup, nextPage, trackEvent]);

  const save = () => {
    if (error) return;

    trackEvent(CommittedClubEvent.NameSaved, {
      app: "committed_club",
      live_now: isClubSetup,
      state: hasLaunched ? "launched" : "pre_launch",
      ai_generated: allGeneratedNames.has(name),
      page: "name_panel",
    });

    setName(name);
    closeDrawer(false);

    if (isClubSetup) {
      updateClub({ title: name });
    }
  };

  return (
    <>
      <Text variant="heading-5" color="black">
        What name do you want to use for your Committed Club?
      </Text>
      <div className="tw-h-4" />

      <TextInput
        name="name"
        value={name}
        onChange={(e) => validateAndSetName(e.target.value)}
        error={!!error}
      />

      <div className="tw-h-3">
        <Text
          variant="body-sm"
          color="wild-watermellon-600"
          className={error ? "tw-block" : "tw-hidden"}
        >
          {error}
        </Text>
      </div>

      <div className="tw-h-2" />

      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <Text weight="600" variant="body-lg" color="black">
          Suggestions
        </Text>
        <Button
          text="Refresh"
          icon={Icons.Refresh}
          iconLocation="right"
          buttonType="text"
          size="small"
          onClick={refreshNames}
        />
      </div>

      <div className="tw-h-1" />

      <div className="tw-flex tw-flex-wrap tw-gap-3">
        {(isLoading || isRefetching) && (
          <div className="tw-container tw-flex tw-justify-center tw-mt-4">
            <Loader />
          </div>
        )}

        {!isError &&
          !isLoading &&
          !isRefetching &&
          (nameSuggestions ?? []).map((suggestion) => (
            <Button
              key={suggestion}
              text={suggestion}
              buttonType="secondary"
              size="small"
              onClick={() => validateAndSetName(suggestion)}
            />
          ))}
      </div>

      {isError && (
        <div className="tw-mt-3">
          <Text weight="400" variant="body-lg" color="wolf-500" align="center">
            Could not retrieve suggestions. Try refreshing.
          </Text>
        </div>
      )}

      <div className="tw-h-8" />

      <Button
        fullWidth
        text="Save"
        size="large"
        buttonType="primary"
        onClick={save}
      />
    </>
  );
}

export default SetupNamePane;
